import React from 'react';

/**
 * 24px icons for the Create menu, shared by the mobile sheet
 * (MobileCreateMenuSheet) and the desktop rail flyout (Navigation).
 *
 * The five action glyphs are the design export from `create_items`, inlined as
 * SVG with `currentColor` so the orange comes from the shared className. The
 * social stack keeps the brand marks (Instagram / Facebook / TikTok) as vectors
 * rather than the 1.4MB PNG-backed export of the same composition.
 */

const iconClassName = 'h-6 w-6 text-orange-500';

/** Recommendation / place — map pin. */
export const PinIcon: React.FC = () => (
  <svg
    viewBox="0 0 40 40"
    className={iconClassName}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path d="M29.3867 8.01512C34.6367 13.1785 34.6367 21.4418 29.4567 26.5368L20.0051 35.8335L10.5501 26.5368C9.32042 25.3353 8.34334 23.9001 7.67626 22.3156C7.00917 20.7312 6.66553 19.0293 6.66553 17.3101C6.66553 15.5909 7.00917 13.8891 7.67626 12.3046C8.34334 10.7201 9.32042 9.28495 10.5501 8.08345L10.6201 8.01512C13.1203 5.54922 16.4909 4.16675 20.0026 4.16675C23.5143 4.16675 26.8865 5.54922 29.3867 8.01512ZM29.3867 8.01512C29.3167 7.94512 29.3167 7.94512 29.3867 8.01512ZM23.3334 15.8335C23.3334 16.7175 22.9822 17.5654 22.3571 18.1905C21.732 18.8156 20.8841 19.1668 20.0001 19.1668C19.116 19.1668 18.2682 18.8156 17.643 18.1905C17.0179 17.5654 16.6667 16.7175 16.6667 15.8335C16.6667 14.9494 17.0179 14.1016 17.643 13.4764C18.2682 12.8513 19.116 12.5001 20.0001 12.5001C20.8841 12.5001 21.732 12.8513 22.3571 13.4764C22.9822 14.1016 23.3334 14.9494 23.3334 15.8335Z" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** My links — chain link. */
export const LinkIcon: React.FC = () => (
  <svg
    viewBox="0 0 32 32"
    className={iconClassName}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path d="M17.3333 7.99992L20 5.33325C21.3333 3.99992 24 3.99992 25.3333 5.33325L26.6667 6.66659C28 7.99992 28 10.6666 26.6667 11.9999L20 18.6666C18.6667 19.9999 16 19.9999 14.6667 18.6666M14.6667 23.9999L12 26.6666C10.6667 27.9999 8.00001 27.9999 6.66668 26.6666L5.33334 25.3333C4.00001 23.9999 4.00001 21.3333 5.33334 19.9999L12 13.3333C13.3333 11.9999 16 11.9999 17.3333 13.3333" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Paste text — document with lines. */
export const DocIcon: React.FC = () => (
  <svg
    viewBox="0 0 32 32"
    className={iconClassName}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path d="M26.9985 13.0002V24.9988C26.9985 26.0595 26.5771 27.0768 25.8271 27.8268C25.0771 28.5769 24.0598 28.9983 22.9991 28.9983H9.00113C7.94042 28.9983 6.92315 28.5769 6.17311 27.8268C5.42307 27.0768 5.00171 26.0595 5.00171 24.9988V7.00097C5.00171 4.80124 6.80145 3.00146 9.00113 3.00146H17.02" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M11.0002 22.9982H20.9992M11.0002 16.9992H20.9992M11.0002 11.0002H13M16.9996 3.00146L26.9985 12.9998H18.9994C18.469 12.9998 17.9604 12.7892 17.5853 12.4141C17.2103 12.0391 16.9996 11.5305 16.9996 11.0002V3.00146Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/** List — bulleted list. */
export const ListIcon: React.FC = () => (
  <svg
    viewBox="0 0 30 30"
    className={iconClassName}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <rect x="6.25" y="13.75" width="23.75" height="2.5" rx="1" fill="currentColor" />
    <rect x="6.25" y="21.25" width="23.75" height="2.5" rx="1" fill="currentColor" />
    <rect x="6.25" y="6.25" width="23.75" height="2.5" rx="1" fill="currentColor" />
    <rect y="13.75" width="3.75" height="2.5" rx="1" fill="currentColor" />
    <rect y="21.25" width="3.75" height="2.5" rx="1" fill="currentColor" />
    <rect y="6.25" width="3.75" height="2.5" rx="1" fill="currentColor" />
  </svg>
);

/** Trip — route between two pins. */
export const TripIcon: React.FC = () => (
  <svg
    viewBox="0 0 30 30"
    className={iconClassName}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path d="M6.76723 27.4817C6.90512 27.4938 7.05102 27.5 7.20302 27.5H7.86708C8.55741 27.5 9.11708 28.0597 9.11708 28.75C9.11708 29.4403 8.55741 30 7.86708 30H7.20302C6.98034 30 6.76145 29.9906 6.54872 29.9719C5.86131 29.9113 5.35317 29.3045 5.41347 28.6169C5.47404 27.9297 6.0799 27.4217 6.76723 27.4817ZM13.1784 27.5C13.8686 27.5001 14.4284 28.0597 14.4284 28.75C14.4284 29.4403 13.8686 29.9999 13.1784 30H11.8515C11.1611 30 10.6015 29.4404 10.6015 28.75C10.6015 28.0596 11.1611 27.5 11.8515 27.5H13.1784ZM17.8256 27.5C18.5159 27.5 19.0756 28.0596 19.0756 28.75C19.0756 29.4404 18.5159 30 17.8256 30H17.1627C16.4724 30 15.9127 29.4404 15.9127 28.75C15.9127 28.0596 16.4724 27.5 17.1627 27.5H17.8256ZM2.64736 25.3442C3.27388 25.0547 4.01653 25.327 4.30629 25.9534C4.40527 26.1675 4.53557 26.3698 4.69814 26.5515C5.15829 27.0661 5.115 27.8564 4.60048 28.3167C4.08597 28.7768 3.29561 28.7322 2.83534 28.2178C2.50396 27.8473 2.23754 27.4369 2.03701 27.0032C1.74735 26.3765 2.0207 25.6339 2.64736 25.3442ZM2.83534 21.5735C3.29564 21.0593 4.08605 21.0157 4.60048 21.4758C5.11466 21.9361 5.15826 22.7265 4.69814 23.241C4.53572 23.4226 4.40521 23.6239 4.30629 23.8379C4.01668 24.4644 3.27393 24.7376 2.64736 24.4482C2.0208 24.1586 1.74758 23.4159 2.03701 22.7893C2.23756 22.3554 2.50386 21.9441 2.83534 21.5735ZM7.67299 19.762C8.35709 19.6692 8.98771 20.1484 9.08046 20.8325C9.17294 21.5163 8.69361 22.1458 8.00991 22.2388C7.7506 22.2739 7.48104 22.2912 7.20302 22.2913C7.05102 22.2913 6.90512 22.2987 6.76723 22.3108C6.07979 22.3708 5.47383 21.8618 5.41347 21.1743C5.35317 20.4868 5.86129 19.8799 6.54872 19.8193C6.76145 19.8007 6.98034 19.7913 7.20302 19.7913C7.3746 19.7912 7.5311 19.7812 7.67299 19.762ZM9.21108 16.2744C9.79383 15.9045 10.5671 16.0763 10.9372 16.6589C11.2907 17.2158 11.4916 17.8389 11.5206 18.4729C11.5521 19.1623 11.0186 19.7472 10.3292 19.7791C9.63978 19.8106 9.05495 19.277 9.02309 18.5876C9.01488 18.4086 8.95735 18.2053 8.82656 17.9993C8.45668 17.4166 8.62866 16.6445 9.21108 16.2744ZM5.95058 14.375C6.20665 14.375 6.45533 14.3851 6.69643 14.4055C7.3842 14.4639 7.89476 15.0691 7.83657 15.7568C7.77818 16.4447 7.1731 16.9553 6.48525 16.897C6.31701 16.8827 6.13881 16.875 5.95058 16.875C5.26022 16.875 4.70058 16.3154 4.70058 15.625C4.70058 14.9346 5.26022 14.375 5.95058 14.375Z" fill="currentColor" />
    <path d="M27.4999 18.7449C27.4998 17.1421 26.8834 15.6205 25.8105 14.5115C24.7402 13.4052 23.3054 12.8001 21.8286 12.8001C20.352 12.8002 18.9181 13.4053 17.8479 14.5115C16.775 15.6205 16.1586 17.1421 16.1584 18.7449C16.1584 19.2538 16.3876 20.0551 17.0056 21.1338C17.6006 22.1723 18.4326 23.256 19.3298 24.2759C20.2229 25.2912 21.1293 26.1849 21.8286 26.8382C22.528 26.1848 23.4352 25.2914 24.3286 24.2759C25.2258 23.256 26.0578 22.1723 26.6528 21.1338C27.2708 20.0551 27.4999 19.2538 27.4999 18.7449ZM29.9938 18.961C29.7663 23.5115 22.4856 29.6199 22.1704 29.8826L22.0935 29.9351C22.013 29.9813 21.9216 30.0059 21.8286 30.0059C21.7358 30.0058 21.6452 29.9812 21.5649 29.9351L21.488 29.8826C21.1677 29.6158 13.6584 23.3156 13.6584 18.7449C13.6586 16.5053 14.5189 14.3569 16.051 12.7732C17.5832 11.1895 19.6618 10.3002 21.8286 10.3C23.9953 10.3 26.0739 11.1897 27.6061 12.7732C29.1383 14.3569 29.9998 16.5052 29.9999 18.7449L29.9938 18.961Z" fill="currentColor" />
    <path d="M9.34448 5.79834C9.34448 4.73818 8.93634 3.73307 8.22876 3.00171C7.52339 2.27275 6.58018 1.87516 5.61035 1.875C4.64031 1.875 3.69624 2.27259 2.99072 3.00171C2.28314 3.73307 1.875 4.73818 1.875 5.79834C1.87501 6.10747 2.01706 6.62826 2.4353 7.3584C2.83665 8.05888 3.4014 8.79501 4.01367 9.49097C4.57732 10.1316 5.14906 10.7007 5.60913 11.1353C6.06931 10.7007 6.6419 10.1319 7.20581 9.49097C7.81808 8.79501 8.38283 8.05888 8.78418 7.3584C9.20243 6.62826 9.34448 6.10747 9.34448 5.79834ZM11.2048 6.09619C10.9019 9.20294 6.05653 13.2673 5.84351 13.4448L5.79102 13.4814C5.73606 13.5129 5.67383 13.529 5.61035 13.5291C5.54666 13.5291 5.4836 13.513 5.42847 13.4814L5.37598 13.4448C5.16295 13.2673 0.317595 9.20294 0.0146484 6.09619L0 5.79834C0 4.35663 0.519619 2.97039 1.45142 1.90674L1.64307 1.698C2.69509 0.610699 4.12262 0 5.61035 0L5.88745 0.00732422C7.27407 0.0783069 8.59004 0.678553 9.57642 1.698C10.6284 2.78539 11.2195 4.26053 11.2195 5.79834L11.2048 6.09619Z" fill="currentColor" />
    <path d="M5.60986 3.979C5.87699 3.97904 6.13988 4.06101 6.36475 4.21631C6.58972 4.37177 6.76789 4.59432 6.87354 4.85791C6.97927 5.12176 7.00674 5.41369 6.95264 5.69482C6.89849 5.97571 6.76554 6.23188 6.57275 6.43115C6.38004 6.63031 6.13604 6.7639 5.87354 6.81787C5.61133 6.87172 5.33941 6.84392 5.09131 6.73779C4.84293 6.63145 4.62761 6.45015 4.4751 6.21436C4.32247 5.97825 4.23975 5.69861 4.23975 5.41162C4.23979 5.02648 4.38822 4.66055 4.64697 4.39307C4.90525 4.1261 5.25197 3.979 5.60986 3.979Z" fill="currentColor" stroke="currentColor" />
    <path d="M23.3424 15.8416C22.8945 15.5323 22.368 15.3672 21.8293 15.3672C21.107 15.3672 20.4142 15.6638 19.9034 16.1917C19.3927 16.7197 19.1057 17.4357 19.1057 18.1823C19.1057 18.7391 19.2654 19.2834 19.5647 19.7463C19.864 20.2093 20.2894 20.5701 20.787 20.7831C21.2847 20.9962 21.8323 21.052 22.3606 20.9433C22.889 20.8347 23.3743 20.5666 23.7551 20.1729C24.136 19.7792 24.3954 19.2776 24.5005 18.7315C24.6056 18.1854 24.5517 17.6194 24.3455 17.105C24.1394 16.5906 23.7903 16.151 23.3424 15.8416Z" fill="currentColor" />
  </svg>
);

/**
 * Stacked Instagram / Facebook / TikTok marks for the "Import from social
 * media" tile: Facebook bottom-left, TikTok bottom-right, Instagram on top.
 * Brand colours, so this one does not follow `currentColor`.
 */
export const SocialStackIcon: React.FC = () => {
  const gradientId = React.useId();
  const igGradient = `${gradientId}-ig`;
  const fbGradient = `${gradientId}-fb`;

  return (
    <svg
      viewBox="0 0 46 40"
      className="h-6 w-[27.6px] shrink-0"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <defs>
        <linearGradient id={igGradient} x1="4%" y1="96%" x2="96%" y2="4%">
          <stop offset="0%" stopColor="#feda75" />
          <stop offset="25%" stopColor="#fa7e1e" />
          <stop offset="50%" stopColor="#d62976" />
          <stop offset="75%" stopColor="#962fbf" />
          <stop offset="100%" stopColor="#4f5bd5" />
        </linearGradient>
        <linearGradient id={fbGradient} x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%" stopColor="#19afff" />
          <stop offset="100%" stopColor="#0062e0" />
        </linearGradient>
      </defs>

      {/* Facebook */}
      <g transform="translate(0 16)">
        <circle cx="12" cy="12" r="12" fill={`url(#${fbGradient})`} />
        <path
          d="M15.12 5.32H17V2.14A26.11 26.11 0 0 0 14.26 2C11.54 2 9.68 3.66 9.68 6.7v2.62H6.61v3.56h3.07V22h3.68v-9.12h3.06l.46-3.56h-3.52V7.05c0-1.03.28-1.73 1.76-1.73Z"
          fill="#fff"
        />
      </g>

      {/* TikTok */}
      <g transform="translate(22 16)">
        <circle cx="12" cy="12" r="12" fill="#010101" />
        <path
          d="M15.13 6.2c.4 1.4 1.4 2.4 2.7 2.7v2.2c-1.1 0-2.1-.3-3-.9v4.6a3.9 3.9 0 1 1-3.9-3.9c.24 0 .47.02.7.06v2.2a1.7 1.7 0 1 0 1.2 1.64V6.2h2.3Z"
          fill="#25f4ee"
        />
        <path
          d="M15.87 6.8c.4 1.4 1.4 2.4 2.7 2.7v2.2c-1.1 0-2.1-.3-3-.9v4.6a3.9 3.9 0 1 1-3.9-3.9c.24 0 .47.02.7.06v2.2a1.7 1.7 0 1 0 1.2 1.64V6.8h2.3Z"
          fill="#fe2c55"
        />
        <path
          d="M15.5 6.5c.4 1.4 1.4 2.4 2.7 2.7v2.2c-1.1 0-2.1-.3-3-.9v4.6a3.9 3.9 0 1 1-3.9-3.9c.24 0 .47.02.7.06v2.2a1.7 1.7 0 1 0 1.2 1.64V6.5h2.3Z"
          fill="#fff"
        />
      </g>

      {/* Instagram */}
      <g transform="translate(11 0)">
        <circle cx="12" cy="12" r="12" fill={`url(#${igGradient})`} />
        <rect
          x="5.9"
          y="5.9"
          width="12.2"
          height="12.2"
          rx="4"
          fill="none"
          stroke="#fff"
          strokeWidth="1.7"
        />
        <circle cx="12" cy="12" r="3.3" fill="none" stroke="#fff" strokeWidth="1.7" />
        <circle cx="15.7" cy="8.3" r="1.05" fill="#fff" />
      </g>
    </svg>
  );
};
